import { CompleteProfileUpdateRequest, EligibilityFailureReason } from '@api/dto';
import ErrorDialog from '@components/ErrorDialog';
import { EMPLOYEE_NOT_ELIGIBLE_ERROR, MISSING_REQUIRED_FIELD_ERROR, REDIRECT_KEY_STORAGE } from '@constants';
import useAuth from '@contexts/AuthProvider';
import { useRegisterContext } from '@contexts/RegisterProvider';
import { Button } from '@mui/material';
import { resolveCommonError } from '@pages/RegisterPage/utils/errorCodeUtils';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';

import BikeleasingForm from '../../forms/BikeleasingForm';
import OnePortalForm from '../../forms/OnePortalForm';
import SkeletonForm from '../../forms/SkeletonForm';
import { useConfirmRegistration } from '../../hooks/useConfirmRegistration';
import { useGetEmployeeQuery } from '../../hooks/useGetEmployeeQuery';
import { useUpdateEmployee } from '../../hooks/useUpdateEmployee';

const EMAIL_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const isEmail = (text?: unknown): text is string => typeof text === 'string' && EMAIL_REGEX.test(text);

export default function CompleteDataForm({ employeeId }: { employeeId: string }) {
  const navigate = useNavigate();
  const { logout, tokenParsed } = useAuth();
  const { registrationCode } = useRegisterContext();
  const getEmployeeQuery = useGetEmployeeQuery(employeeId);
  const updateEmployee = useUpdateEmployee(employeeId);
  const { confirm, isConfirming } = useConfirmRegistration();
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const showErrorDialog = getEmployeeQuery.isError || !employeeId;

  const completeData = useCallback(
    async (data: CompleteProfileUpdateRequest) => {
      setError(null);
      setFieldError(null);
      const [fallbackMail] = [tokenParsed?.preferred_username, tokenParsed?.email].filter(isEmail);
      // 12 digit registration code needs to set contact mail otherwise the as rejects the employee
      if (!getEmployeeQuery.data?.emailAddress && fallbackMail) {
        data.emailAddress = fallbackMail;
      }
      try {
        await updateEmployee.mutateAsync(data);
        await confirm(tokenParsed?.sub);
        void navigate('/success');
      } catch (err) {
        let updateEmployeeError: string;
        if (err instanceof AxiosError && err.response?.status === 409) {
          updateEmployeeError = 'completeData.errors.conflict';
        } else if (err instanceof AxiosError && err.response?.status === 400 && err.response.data?.type === EMPLOYEE_NOT_ELIGIBLE_ERROR) {
          const reason = err.response.data.eligibilityFailureReason as EligibilityFailureReason | undefined;
          updateEmployeeError = `completeData.errors.notEligible.${reason ?? EligibilityFailureReason.EligibilityCheckFailed}`;
        } else if (err instanceof AxiosError && err.response?.status === 400 && err.response.data?.type === MISSING_REQUIRED_FIELD_ERROR) {
          updateEmployeeError = 'completeData.errors.missingRequiredField';
          const detail: string | undefined = err.response.data?.detail;
          const match = detail?.match(/"(.+?)"/);
          setFieldError(match?.[1] ?? null);
        } else {
          updateEmployeeError = resolveCommonError(err);
        }
        setError(updateEmployeeError);
      }
    },
    [confirm, getEmployeeQuery.data?.emailAddress, navigate, tokenParsed, updateEmployee],
  );

  const loginAgain = async () => {
    sessionStorage.setItem(REDIRECT_KEY_STORAGE, registrationCode ? '/auth' : '/complete/resume');
    try {
      await logout({ logoutMethod: 'POST', redirectUri: `${window.location.origin}/oauth` });
    } catch {
      await logout({ logoutMethod: 'GET', redirectUri: `${window.location.origin}/oauth` });
    }
  };

  return (
    <>
      <ErrorDialog
        open={showErrorDialog}
        errorKey={error}
        body={<Trans i18nKey="errorDialog.messages.employeeError" />}
        primaryAction={
          <Button onClick={loginAgain}>
            <Trans i18nKey="errorDialog.actions.loginAgain" />
          </Button>
        }
      />
      {!getEmployeeQuery.isSuccess ? (
        <SkeletonForm />
      ) : getEmployeeQuery.data?.companySettings.registerValidation ? (
        <BikeleasingForm
          employeeId={employeeId}
          error={error}
          fieldError={fieldError}
          showLoadingButton={updateEmployee.isPending || isConfirming}
          onSubmit={completeData}
        />
      ) : (
        <OnePortalForm
          employeeId={employeeId}
          error={error}
          fieldError={fieldError}
          showLoadingButton={updateEmployee.isPending || isConfirming}
          onSubmit={completeData}
        />
      )}
    </>
  );
}
